import { useMemo, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/AddRounded';
import { Input } from '@components/Input';
import { MultiSelect } from '@components/MultiSelect';
import Button from '@components/Button';
import { InlineFieldsRow, rowDeleteAction } from '@components/FieldWithActions';
import TimeframePicker from '@components/TimeframePicker';
import AIAssistant, { AIAssistantLoadingWrapper } from '@features/AIAssistant';
import { useAiDraftedFields } from '@features/locations/optimizationScore/ScoreAssistantProvider';
import { useTranslation } from '@hooks';
import { LocationType } from '@constants/companies';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { Industry } from '@constants/account';

const Form = styled('div')({
    maxWidth: 698,
    display: 'grid',
    rowGap: 16,
    gridTemplateColumns: 'minmax(0, 1fr)',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 'none',
    },
});

const FormFields = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gap: 16,
    '@media (max-width: 1024px)': {
        gridTemplateColumns: '100%',
    },
});

const DescriptionField = styled('div', { shouldForwardProp: prop => prop !== 'aiDrafted' })(({ aiDrafted }) => ({
    position: 'relative',
    ...(aiDrafted && {
        '& .MuiOutlinedInput-root:not(.Mui-error) fieldset': {
            borderColor: 'var(--ai-gradient-mid) !important',
            borderWidth: '2px !important',
        },
    }),
}));

const AdditionalInfo = ({ industry, companyId, menus }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext();
    const aiDraftedFields = useAiDraftedFields();
    const featuredMessageEnabled = watch('featuredMessageEnabled');
    const type = watch('type');
    const [aiLoadingField, setAiLoadingField] = useState(null);

    const menuOptions = useMemo(
        () => (menus || []).map(menu => ({ value: menu.id, label: menu.internalName })),
        [menus],
    );
    const showMenus = [LocationType.Hotel, LocationType.Restaurant].includes(type) && menuOptions.length > 0;

    return (
        <Form>
            <Controller
                control={control}
                name="shortDescription"
                render={({ field, fieldState: { error }, ref }) => (
                    <DescriptionField aiDrafted={aiDraftedFields.includes('shortDescription')}>
                        {aiLoadingField === 'shortDescription' && <AIAssistantLoadingWrapper />}
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.descriptionShort')}
                            fullWidth
                            multiline
                            minRows={2}
                            maxLength={75}
                            error={!!error}
                            helperText={error ? t(error.message, { count: 75 }) : null}
                            disabled={aiLoadingField === 'shortDescription'}
                            toolbar={
                                <AIAssistant
                                    btnStyles={{ position: 'absolute', left: 12, bottom: 32.5 }}
                                    value={field.value}
                                    setValue={newValue => field.onChange(newValue)}
                                    disabled={!!aiLoadingField}
                                    setLoading={loading => setAiLoadingField(loading ? 'shortDescription' : null)}
                                    additionalOptions={[
                                        {
                                            text: t('AIAssistant.writeDescription'),
                                            type: AITaskType.GenerateGoogleLocationShortDescription,
                                            taskType: AI_TASK_TYPE.CONTEXTUAL,
                                            companyId,
                                        },
                                    ]}
                                />
                            }
                        />
                    </DescriptionField>
                )}
            />
            <Controller
                control={control}
                name="mediumDescription"
                render={({ field, fieldState: { error }, ref }) => (
                    <DescriptionField aiDrafted={aiDraftedFields.includes('mediumDescription')}>
                        {aiLoadingField === 'mediumDescription' && <AIAssistantLoadingWrapper />}
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.descriptionMedium')}
                            fullWidth
                            multiline
                            minRows={3}
                            maxLength={500}
                            error={!!error}
                            helperText={error ? t(error.message, { count: 500 }) : null}
                            disabled={aiLoadingField === 'mediumDescription'}
                            toolbar={
                                <AIAssistant
                                    btnStyles={{ position: 'absolute', left: 12, bottom: 32.5 }}
                                    value={field.value}
                                    setValue={newValue => field.onChange(newValue)}
                                    disabled={!!aiLoadingField}
                                    setLoading={loading => setAiLoadingField(loading ? 'mediumDescription' : null)}
                                    additionalOptions={[
                                        {
                                            text: t('AIAssistant.writeDescription'),
                                            type: AITaskType.GenerateGoogleLocationMediumDescription,
                                            taskType: AI_TASK_TYPE.CONTEXTUAL,
                                            companyId,
                                        },
                                    ]}
                                />
                            }
                        />
                    </DescriptionField>
                )}
            />
            <Controller
                control={control}
                name="longDescription"
                render={({ field, fieldState: { error }, ref }) => (
                    <DescriptionField aiDrafted={aiDraftedFields.includes('longDescription')}>
                        {aiLoadingField === 'longDescription' && <AIAssistantLoadingWrapper />}
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.descriptionLong')}
                            fullWidth
                            multiline
                            minRows={4}
                            maxLength={750}
                            error={!!error}
                            helperText={error ? t(error.message, { count: 750 }) : null}
                            disabled={aiLoadingField === 'longDescription'}
                            toolbar={
                                <AIAssistant
                                    btnStyles={{ position: 'absolute', left: 12, bottom: 32.5 }}
                                    value={field.value}
                                    setValue={newValue => field.onChange(newValue)}
                                    disabled={!!aiLoadingField}
                                    setLoading={loading => setAiLoadingField(loading ? 'longDescription' : null)}
                                    additionalOptions={[
                                        {
                                            text: t('AIAssistant.writeDescription'),
                                            type: AITaskType.GenerateGoogleLocationDescription,
                                            taskType: AI_TASK_TYPE.CONTEXTUAL,
                                            companyId,
                                        },
                                    ]}
                                />
                            }
                        />
                    </DescriptionField>
                )}
            />
            <FormFields>
                {industry === Industry.HealthcareAndDental && type === LocationType.HealthcareProfessional && (
                    <Controller
                        control={control}
                        name="office"
                        render={({ field, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsCompanies.office')}
                                fullWidth
                            />
                        )}
                    />
                )}
                <Controller
                    control={control}
                    name="openingDate"
                    render={({ field }) => (
                        <TimeframePicker
                            value={[field.value]}
                            range={false}
                            label={t('Listings.openingDate')}
                            onChangeTimeframe={value => {
                                setValue('openingDate', value, { shouldDirty: true });
                            }}
                            minDate={null}
                            maxDate={new Date()}
                        />
                    )}
                />
                {showMenus ? (
                    <Controller
                        control={control}
                        name="menuIds"
                        render={({ field }) => (
                            <MultiSelect
                                value={field.value || []}
                                onChange={selected =>
                                    setValue('menuIds', selected, { shouldDirty: true, shouldTouch: true })
                                }
                                options={menuOptions}
                                label={t('SettingsCompanies.menus')}
                                countable
                            />
                        )}
                    />
                ) : (
                    <div />
                )}
            </FormFields>
            {featuredMessageEnabled && (
                <InlineFieldsRow
                    actions={rowDeleteAction({
                        onDelete: () => {
                            setValue('featuredMessage', '', { shouldValidate: true, shouldDirty: true });
                            setValue('featuredMessageUrl', '', { shouldValidate: true, shouldDirty: true });
                            setValue('featuredMessageEnabled', false, { shouldValidate: true, shouldDirty: true });
                        },
                        t,
                    })}
                >
                    <Controller
                        control={control}
                        name="featuredMessage"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.message', { count: 1 })}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message, { count: 50 }) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="featuredMessageUrl"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.url')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message, { count: 255 }) : null}
                                required
                            />
                        )}
                    />
                </InlineFieldsRow>
            )}
            <Button
                variant="outlined"
                sx={{ width: 'max-content' }}
                startIcon={<AddIcon />}
                disabled={featuredMessageEnabled}
                onClick={() => setValue('featuredMessageEnabled', true, { shouldValidate: true })}
            >
                {t('SettingsCompanies.addFeatureMessage')}
            </Button>
        </Form>
    );
};

export default AdditionalInfo;
